import Box from "@/elements/Box";
import NumberInput from "@/elements/Input/NumberInput";
import {
  FormHelperText,
  Grid,
  InputAdornment,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import TextField from "@mui/material/TextField";
import Card from "@mui/material/Card";
import {
  Control,
  Controller,
  FieldErrors,
  UseFormGetValues,
  UseFormRegister,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import { RegistroDto, UpdateRegistroDto } from "@/api";
import { isNil, keyBy } from "lodash";

export type Registri_GFL_TLV_300 = {
  sensorewt: boolean;
  allbassaT: number;
  allaltaT: number;
  allPmaxF: number;
  allPminF: number;
  allPminTF: number;
  allPmaxM: number;
  allPminM: number;
  allPminTM: number;
  email1: string;
  email2: string;
  funzmanpower: number;
};

export function registriToForm_GFL_TLV_300(
  _registri: RegistroDto[]
): Partial<Registri_GFL_TLV_300> {
  const registri = keyBy(_registri, "registro");
  return {
    sensorewt:
      registri["sensorewt"]?.value === "1" ||
      registri["sensorewt"]?.value === "true",
    allbassaT: isNil(registri["allbassaT"])
      ? undefined
      : parseFloat(registri["allbassaT"].value),
    allaltaT: isNil(registri["allaltaT"])
      ? undefined
      : parseFloat(registri["allaltaT"].value),
    allPmaxF: isNil(registri["allPmaxF"])
      ? undefined
      : parseFloat(registri["allPmaxF"].value),
    allPminF: isNil(registri["allPminF"])
      ? undefined
      : parseFloat(registri["allPminF"].value),
    allPminTF: isNil(registri["allPminTF"])
      ? undefined
      : parseFloat(registri["allPminTF"].value),
    allPmaxM: isNil(registri["allPmaxM"])
      ? undefined
      : parseFloat(registri["allPmaxM"].value),
    allPminM: isNil(registri["allPminM"])
      ? undefined
      : parseFloat(registri["allPminM"].value),
    allPminTM: isNil(registri["allPminTM"])
      ? undefined
      : parseFloat(registri["allPminTM"].value),
    email1: registri["email1"]?.value || "",
    email2: registri["email2"]?.value || "",
    funzmanpower: isNil(registri["funzmanpower"])
      ? undefined
      : parseFloat(registri["funzmanpower"].value),
  };
}

export function formToRegistri_GFL_TLV_300(form: any): UpdateRegistroDto[] {
  const topicPrefix = "var";
  const _values: {
    [k in keyof Registri_GFL_TLV_300]?: UpdateRegistroDto;
  } = {
    sensorewt: isNil(form.sensorewt)
      ? undefined
      : {
          registro: "sensorewt",
          topicPrefix,
          value: form.sensorewt ? "1" : "0",
        },
    allbassaT: isNil(form.allbassaT)
      ? undefined
      : {
          registro: "allbassaT",
          topicPrefix,
          value: form.allbassaT,
        },
    allaltaT: isNil(form.allaltaT)
      ? undefined
      : {
          registro: "allaltaT",
          topicPrefix,
          value: form.allaltaT,
        },
    allPmaxF: isNil(form.allPmaxF)
      ? undefined
      : {
          registro: "allPmaxF",
          topicPrefix,
          value: form.allPmaxF,
        },
    allPminF: isNil(form.allPminF)
      ? undefined
      : {
          registro: "allPminF",
          topicPrefix,
          value: form.allPminF,
        },
    allPminTF: isNil(form.allPminTF)
      ? undefined
      : {
          registro: "allPminTF",
          topicPrefix,
          value: form.allPminTF,
        },
    allPmaxM: isNil(form.allPmaxM)
      ? undefined
      : {
          registro: "allPmaxM",
          topicPrefix,
          value: form.allPmaxM,
        },
    allPminM: isNil(form.allPminM)
      ? undefined
      : {
          registro: "allPminM",
          topicPrefix,
          value: form.allPminM,
        },
    allPminTM: isNil(form.allPminTM)
      ? undefined
      : {
          registro: "allPminTM",
          topicPrefix,
          value: form.allPminTM,
        },
    email1: isNil(form.email1)
      ? undefined
      : {
          registro: "email1",
          topicPrefix,
          value: mapEmptyStringToSlash(form.email1),
        },
    email2: isNil(form.email2)
      ? undefined
      : {
          registro: "email2",
          topicPrefix,
          value: mapEmptyStringToSlash(form.email2),
        },
    funzmanpower: isNil(form.funzmanpower)
      ? undefined
      : {
          registro: "funzmanpower",
          topicPrefix,
          value: form.funzmanpower,
        },
  };
  return Object.values(_values).filter(Boolean) as UpdateRegistroDto[];
}

function mapEmptyStringToSlash(value: string) {
  if (value === "") {
    return "/";
  }
  return value;
}

export function EditorRegistri_GFL_TLV_300({
  control,
  errors,
  register,
  getValues,
  readOnly = false,
}: {
  control: Control<Registri_GFL_TLV_300>;
  errors: FieldErrors<Registri_GFL_TLV_300>;
  register: UseFormRegister<Registri_GFL_TLV_300>;
  getValues: UseFormGetValues<Registri_GFL_TLV_300>;
  readOnly?: boolean;
}) {
  const { t } = useTranslation();
  console.log("errors", errors);
  return (
    <Box display="flex" flexDirection="column" gap={2}>
      {/* Sensor WT */}
      <Card>
        <Box p={2} flex={1}>
          <Box display="flex" flexDirection="column" gap={1}>
            <Typography
              variant="h5"
              color="text"
              fontWeight="medium"
              gutterBottom
            >
              {t("gflTlv300.sezioni.sensoreWt")}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="sensorewt"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Typography
                        variant="button"
                        fontWeight="regular"
                        color="text"
                        mr={2}
                        sx={{ minWidth: 160 }}
                      >
                        {t("gflTlv300.campi.abilitato")}
                      </Typography>
                      <Stack direction="column" alignItems="end">
                        <Switch
                          {...field}
                          checked={field.value || false}
                          disabled={readOnly}
                        />
                        {error && (
                          <FormHelperText error>
                            {error?.message}
                          </FormHelperText>
                        )}
                      </Stack>
                    </Stack>
                  )}
                />
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Card>

      {/* Allarmi Temperatura */}
      <Card>
        <Box p={2} flex={1}>
          <Box display="flex" flexDirection="column" gap={2}>
            <Typography
              variant="h5"
              color="text"
              fontWeight="medium"
              gutterBottom
            >
              {t("gflTlv300.sezioni.allarmiTemperatura")}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Typography
                    variant="button"
                    fontWeight="regular"
                    color="text"
                    mr={2}
                    sx={{ minWidth: 160 }}
                  >
                    {t("gflTlv300.campi.allarmeBassaTemperatura")}
                  </Typography>
                  <Controller
                    control={control}
                    name="allbassaT"
                    render={({ field, fieldState: { error } }) => (
                      <Stack direction="column" alignItems="end">
                        <NumberInput
                          {...field}
                          sx={{ maxWidth: 120 }}
                          endAdornment={
                            <InputAdornment position="end">{t("unita.celsius")}</InputAdornment>
                          }
                          numericInputProps={{ decimalScale: 1 }}
                          disabled={readOnly}
                        />
                        {error && (
                          <FormHelperText error>
                            {error?.message}
                          </FormHelperText>
                        )}
                      </Stack>
                    )}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Typography
                    variant="button"
                    fontWeight="regular"
                    color="text"
                    mr={2}
                    sx={{ minWidth: 160 }}
                  >
                    {t("gflTlv300.campi.allarmeAltaTemperatura")}
                  </Typography>
                  <Controller
                    control={control}
                    name="allaltaT"
                    render={({ field, fieldState: { error } }) => (
                      <Stack direction="column" alignItems="end">
                        <NumberInput
                          {...field}
                          sx={{ maxWidth: 120 }}
                          endAdornment={
                            <InputAdornment position="end">{t("unita.celsius")}</InputAdornment>
                          }
                          numericInputProps={{ decimalScale: 1 }}
                          disabled={readOnly}
                        />
                        {error && (
                          <FormHelperText error>
                            {error?.message}
                          </FormHelperText>
                        )}
                      </Stack>
                    )}
                  />
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Card>

      {/* Allarmi Pressione Filtrazione */}
      <Card>
        <Box p={2} flex={1}>
          <Box display="flex" flexDirection="column" gap={2}>
            <Typography
              variant="h5"
              color="text"
              fontWeight="medium"
              gutterBottom
            >
              {t("gflTlv300.sezioni.pressioneFiltrazione")}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Typography
                    variant="button"
                    fontWeight="regular"
                    color="text"
                    mr={2}
                    sx={{ minWidth: 120 }}
                  >
                    {t("gflTlv300.campi.pressioneMinima")}
                  </Typography>
                  <Controller
                    control={control}
                    name="allPminF"
                    render={({ field, fieldState: { error } }) => (
                      <Stack direction="column" alignItems="end">
                        <NumberInput
                          {...field}
                          sx={{ maxWidth: 120 }}
                          endAdornment={
                            <InputAdornment position="end">{t("unita.bar")}</InputAdornment>
                          }
                          numericInputProps={{ decimalScale: 2 }}
                          disabled={readOnly}
                        />
                        {error && (
                          <FormHelperText error>
                            {error?.message}
                          </FormHelperText>
                        )}
                      </Stack>
                    )}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Typography
                    variant="button"
                    fontWeight="regular"
                    color="text"
                    mr={2}
                    sx={{ minWidth: 120 }}
                  >
                    {t("gflTlv300.campi.pressioneMassima")}
                  </Typography>
                  <Controller
                    control={control}
                    name="allPmaxF"
                    render={({ field, fieldState: { error } }) => (
                      <Stack direction="column" alignItems="end">
                        <NumberInput
                          {...field}
                          sx={{ maxWidth: 120 }}
                          endAdornment={
                            <InputAdornment position="end">{t("unita.bar")}</InputAdornment>
                          }
                          numericInputProps={{ decimalScale: 2 }}
                          disabled={readOnly}
                        />
                        {error && (
                          <FormHelperText error>
                            {error?.message}
                          </FormHelperText>
                        )}
                      </Stack>
                    )}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Typography
                    variant="button"
                    fontWeight="regular"
                    color="text"
                    mr={2}
                    sx={{ minWidth: 120 }}
                  >
                    {t("gflTlv300.campi.pressioneMinTemp")}
                  </Typography>
                  <Controller
                    control={control}
                    name="allPminTF"
                    render={({ field, fieldState: { error } }) => (
                      <Stack direction="column" alignItems="end">
                        <NumberInput
                          {...field}
                          sx={{ maxWidth: 120 }}
                          endAdornment={
                            <InputAdornment position="end">{t("unita.secondiAbbreviazione")}</InputAdornment>
                          }
                          numericInputProps={{ decimalScale: 2 }}
                          disabled={readOnly}
                        />
                        {error && (
                          <FormHelperText error>
                            {error?.message}
                          </FormHelperText>
                        )}
                      </Stack>
                    )}
                  />
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Card>

      {/* Allarmi Pressione Mandata */}
      <Card>
        <Box p={2} flex={1}>
          <Box display="flex" flexDirection="column" gap={2}>
            <Typography
              variant="h5"
              color="text"
              fontWeight="medium"
              gutterBottom
            >
              {t("gflTlv300.sezioni.pressioneMandata")}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Typography
                    variant="button"
                    fontWeight="regular"
                    color="text"
                    mr={2}
                    sx={{ minWidth: 120 }}
                  >
                    {t("gflTlv300.campi.pressioneMinima")}
                  </Typography>
                  <Controller
                    control={control}
                    name="allPminM"
                    render={({ field, fieldState: { error } }) => (
                      <Stack direction="column" alignItems="end">
                        <NumberInput
                          {...field}
                          sx={{ maxWidth: 120 }}
                          endAdornment={
                            <InputAdornment position="end">{t("unita.bar")}</InputAdornment>
                          }
                          numericInputProps={{ decimalScale: 2 }}
                          disabled={readOnly}
                        />
                        {error && (
                          <FormHelperText error>
                            {error?.message}
                          </FormHelperText>
                        )}
                      </Stack>
                    )}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Typography
                    variant="button"
                    fontWeight="regular"
                    color="text"
                    mr={2}
                    sx={{ minWidth: 120 }}
                  >
                    {t("gflTlv300.campi.pressioneMassima")}
                  </Typography>
                  <Controller
                    control={control}
                    name="allPmaxM"
                    render={({ field, fieldState: { error } }) => (
                      <Stack direction="column" alignItems="end">
                        <NumberInput
                          {...field}
                          sx={{ maxWidth: 120 }}
                          endAdornment={
                            <InputAdornment position="end">{t("unita.bar")}</InputAdornment>
                          }
                          numericInputProps={{ decimalScale: 2 }}
                          disabled={readOnly}
                        />
                        {error && (
                          <FormHelperText error>
                            {error?.message}
                          </FormHelperText>
                        )}
                      </Stack>
                    )}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Typography
                    variant="button"
                    fontWeight="regular"
                    color="text"
                    mr={2}
                    sx={{ minWidth: 120 }}
                  >
                    {t("gflTlv300.campi.pressioneMinTemp")}
                  </Typography>
                  <Controller
                    control={control}
                    name="allPminTM"
                    render={({ field, fieldState: { error } }) => (
                      <Stack direction="column" alignItems="end">
                        <NumberInput
                          {...field}
                          sx={{ maxWidth: 120 }}
                          endAdornment={
                            <InputAdornment position="end">{t("unita.secondiAbbreviazione")}</InputAdornment>
                          }
                          disabled={readOnly}
                        />
                        {error && (
                          <FormHelperText error>
                            {error?.message}
                          </FormHelperText>
                        )}
                      </Stack>
                    )}
                  />
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Card>

      {/* Email */}
      <Card>
        <Box p={2} flex={1}>
          <Box display="flex" flexDirection="column" gap={2}>
            <Typography
              variant="h5"
              color="text"
              fontWeight="medium"
              gutterBottom
            >
              {t("gflTlv300.sezioni.email")}
            </Typography>
            <Grid container spacing={2}>
              <Grid item sm={12}>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Typography
                    variant="button"
                    fontWeight="regular"
                    color="text"
                    mr={2}
                    sx={{ minWidth: 100 }}
                  >
                    {t("gflTlv300.campi.email1")}
                  </Typography>
                  <TextField
                    {...register("email1")}
                    error={!!errors.email1?.message}
                    helperText={errors.email1?.message || null}
                    fullWidth
                    disabled={readOnly}
                  />
                </Box>
              </Grid>
              <Grid item sm={12}>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Typography
                    variant="button"
                    fontWeight="regular"
                    color="text"
                    mr={2}
                    sx={{ minWidth: 100 }}
                  >
                    {t("gflTlv300.campi.email2")}
                  </Typography>
                  <TextField
                    {...register("email2")}
                    error={!!errors.email2?.message}
                    helperText={errors.email2?.message || null}
                    fullWidth
                    disabled={readOnly}
                  />
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Card>

      {/* Funzionamento manuale */}
      <Card>
        <Box p={2} flex={1}>
          <Box display="flex" flexDirection="column" gap={2}>
            <Typography
              variant="h5"
              color="text"
              fontWeight="medium"
              gutterBottom
            >
              {t("gflTlv300.sezioni.funzionamentoManuale")}
            </Typography>

            <Grid item xs={12} sm={6}>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography
                  variant="button"
                  fontWeight="regular"
                  color="text"
                  mr={2}
                  sx={{ minWidth: 100 }}
                >
                  {t("gflTlv300.campi.potenzaMotore")}
                </Typography>
                <Controller
                  name="funzmanpower"
                  control={control}
                  rules={{
                    min: {
                      value: 0,
                      message: t("gflTlv300.errori.intervalloPercentuale", {
                        min: 0,
                        max: 100,
                      }),
                    },
                    max: {
                      value: 100,
                      message: t("gflTlv300.errori.intervalloPercentuale", {
                        min: 0,
                        max: 100,
                      }),
                    },
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <Stack direction="column" alignItems="end">
                      <NumberInput
                        {...field}
                        sx={{ maxWidth: 120 }}
                        endAdornment={
                          <InputAdornment position="end">{t("unita.percento")}</InputAdornment>
                        }
                        numericInputProps={{
                          decimalScale: 0,
                        }}
                        disabled={readOnly}
                      />
                      {error && (
                        <FormHelperText error>{error?.message}</FormHelperText>
                      )}
                    </Stack>
                  )}
                />
              </Box>
            </Grid>
          </Box>
        </Box>
      </Card>
    </Box>
  );
}
